import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Icons } from '@shared/constants';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import type { GoalCelebration } from '../../hooks/useGoalCelebrations';
import { useRemoteConfig } from '../../context/RemoteConfigContext';
import { ShareComposerSheetLazy } from '../share/ShareComposerSheetLazy';
import { momentFromGoal } from '../share/shareMoments';
import { useShareCardTemplates } from '../../hooks/useShareCardTemplates';
import type { ShareMoment } from '@shared/shareCards/shareCardTypes';

interface GoalCelebrationModalProps {
  celebrations: GoalCelebration[];
  isOpen: boolean;
  onClose: () => void;
  onMarkSeen: (celebration: GoalCelebration) => void;
  isReplay?: boolean;
}

// Clay (#cd7857) — the celebration's accent (the CTA button).
const EFFORT_COLOR = '#cd7857';

// Floating seed images
const SEED_SRCS = [
  '/assets/icons/seeds/1_branch.svg',
  '/assets/icons/seeds/2_branch.svg',
  '/assets/icons/seeds/3_branch.svg',
  '/assets/icons/seeds/4_branch.svg',
];

interface FloatingSeed {
  id: number;
  src: string;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  delay: number;
  duration: number;
  size: number;
  drift: number;
  rotation: number;
}

const generateSeeds = (count: number): FloatingSeed[] =>
  Array.from({ length: count }, (_, i) => {
    const angle = (Math.random() - 0.5) * 0.8;
    return {
      id: i,
      src: SEED_SRCS[i % SEED_SRCS.length],
      startX: 50 + (Math.random() - 0.5) * 12,
      startY: 68 + (Math.random() - 0.5) * 6,
      endX: 50 + Math.sin(angle) * 18,
      endY: 18 + (Math.random() - 0.5) * 8,
      delay: 0.2 + Math.random() * 1.4,
      duration: 1.6 + Math.random() * 1.2,
      size: 20 + Math.random() * 16,
      drift: (Math.random() - 0.5) * 40,
      rotation: Math.random() * 360,
    };
  });

// Headline based on outcome tier
const getHeadline = (c: GoalCelebration): string => {
  if (c.success) return 'You achieved your goal!';
  if (c.goal.progressPercent >= 60) return 'You nearly met your goal.';
  return "You didn\u2019t meet your goal this time.";
};

// Body copy based on outcome tier
const getBody = (c: GoalCelebration): string => {
  if (c.success) {
    return 'Take a moment to celebrate your growth \u2013 then build on this momentum.';
  }
  if (c.goal.progressPercent >= 60) {
    return `${c.goal.progressPercent.toFixed(0)}% progress toward ${c.goal.title}. That\u2019s real change \u2013 keep challenging yourself with new goals.`;
  }
  return `It's okay. Setting goals still counts as effort. Consider what you could have done differently. Then, find a new goal to get excited about!`;
};

type ModalPhase = 'hidden' | 'entering' | 'filling' | 'seeds' | 'visible' | 'exiting';

export const GoalCelebrationModal: React.FC<GoalCelebrationModalProps> = ({
  celebrations,
  isOpen,
  onClose,
  onMarkSeen,
  isReplay = false,
}) => {
  const [modalPhase, setModalPhase] = useState<ModalPhase>('hidden');
  const [index, setIndex] = useState(0);
  const [textVisible, setTextVisible] = useState(false);
  const [seedKey, setSeedKey] = useState(0);
  const [sequenceId, setSequenceId] = useState(0); // bumped to trigger phase sequencing
  const [shareMoment, setShareMoment] = useState<ShareMoment | null>(null);
  const { shareCardsEnabled } = useRemoteConfig();
  const shareTemplates = useShareCardTemplates(shareCardsEnabled);

  const total = celebrations.length;
  const current = celebrations[index] ?? null;

  const seeds = useMemo(
    () => generateSeeds(current?.success ? 8 : 4),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [seedKey]
  );

  // Modal open/close lifecycle
  useEffect(() => {
    if (isOpen && celebrations.length > 0) {
      setIndex(0);
      setTextVisible(false);
      setModalPhase('entering');
      setSeedKey(k => k + 1);
      setSequenceId(s => s + 1); // trigger phase sequencing
    } else if (!isOpen && modalPhase !== 'hidden') {
      setModalPhase('exiting');
      setTimeout(() => setModalPhase('hidden'), 400);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Phase sequencing — runs on open (sequenceId bump) and on index advance
  useEffect(() => {
    if (!current || modalPhase === 'hidden' || modalPhase === 'exiting') return;

    setTextVisible(false);
    setModalPhase('entering');

    const isFirst = index === 0;
    const timers = [
      // Beat before the text lands (was the effort-ring fill)
      setTimeout(() => {
        setModalPhase('filling');
      }, isFirst ? 300 : 200),
      // Text fades in
      setTimeout(() => {
        setTextVisible(true);
        setModalPhase('visible');
      }, isFirst ? 1200 : 800),
      // Seeds fly last, after everything is visible
      setTimeout(() => {
        setSeedKey(k => k + 1);
        setModalPhase('seeds');
        if (current.success) {
          Haptics.notification({ type: NotificationType.Success }).catch(() => {});
        } else {
          Haptics.impact({ style: ImpactStyle.Medium }).catch(() => {});
        }
      }, isFirst ? 1800 : 1200),
    ];

    return () => {
      timers.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sequenceId, index]);

  const handleNext = useCallback(() => {
    if (!current) return;

    // Mark seen
    onMarkSeen(current);

    if (index < total - 1) {
      // Cross-fade to next: fade out text, advance index (which triggers new phase sequence)
      setTextVisible(false);
      setTimeout(() => setIndex(i => i + 1), 300);
    } else {
      // Last one — close modal
      setModalPhase('exiting');
      setTimeout(() => {
        onClose();
        setModalPhase('hidden');
      }, 400);
    }
  }, [current, index, total, onMarkSeen, onClose]);

  const handleClose = useCallback(() => {
    // Mark current as seen, then close
    if (current) onMarkSeen(current);
    setModalPhase('exiting');
    setTimeout(() => {
      onClose();
      setModalPhase('hidden');
    }, 400);
  }, [current, onMarkSeen, onClose]);

  if (modalPhase === 'hidden' || !current) return null;

  const headline = getHeadline(current);
  const body = getBody(current);
  const showSeeds = modalPhase === 'seeds';
  const remaining = total - index - 1;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden"
      onClick={modalPhase === 'visible' || modalPhase === 'seeds' ? handleClose : undefined}
    >
      {/* Backdrop — stays for entire session */}
      <div
        className={`absolute inset-0 transition-opacity duration-500 ${
          modalPhase === 'exiting' ? 'opacity-0' : 'opacity-100'
        }`}
        style={{
          background: 'radial-gradient(ellipse at center, rgba(205,120,87,0.08) 0%, rgba(244,240,232,0.95) 30%, rgba(244,240,232,0.98) 100%)',
          backdropFilter: 'blur(8px)',
        }}
      />

      {/* Floating seeds — re-keyed per item */}
      {showSeeds && (
        <div key={seedKey} className="absolute inset-0 pointer-events-none">
          {seeds.map((s) => (
            <div
              key={s.id}
              className="absolute"
              style={{
                left: `${s.startX}%`,
                top: `${s.startY}%`,
                opacity: 0,
                animation: `goalSeedFloat ${s.duration}s ease-in-out ${s.delay}s forwards`,
                '--seed-end-x': `${s.endX - s.startX}vw`,
                '--seed-end-y': `${s.endY - s.startY}vh`,
                '--seed-drift': `${s.drift}px`,
                '--seed-rotation': `${s.rotation}deg`,
              } as React.CSSProperties}
            >
              <img
                src={s.src}
                alt=""
                className="pointer-events-none"
                style={{
                  width: s.size,
                  height: s.size,
                  filter: `drop-shadow(0 0 ${s.size * 0.3}px rgba(205,120,87,0.3))`,
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Central content */}
      <div
        className={`relative z-10 flex flex-col items-center px-8 max-w-sm w-full transition-all duration-500 ${
          modalPhase === 'exiting' ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Text content — fades between items */}
        <div
          className={`text-center transition-opacity duration-400 ${
            textVisible ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <h2 className="text-2xl font-serif text-growth mb-1 leading-tight">{headline}</h2>
          <p className="text-sm font-medium text-growth/80 mb-2 ph-mask">{current.goal.title}</p>
          <p className="text-sm text-growth/70 leading-relaxed mb-4 max-w-xs mx-auto ph-mask">{body}</p>

          <div className={`transition-opacity duration-700 ${textVisible ? 'opacity-60' : 'opacity-0'}`}>
            <img
              src="/assets/icons/seeds/dandelion_growth.png"
              alt=""
              className="w-16 h-16 mx-auto"
            />
          </div>
        </div>

        {/* Action button — stays mounted, just fades */}
        <div className={`mt-6 flex flex-col items-center gap-3 w-full transition-opacity duration-400 ${
          modalPhase === 'visible' || modalPhase === 'seeds' ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}>
          <button
            onClick={handleNext}
            className="w-full py-3 rounded-2xl font-medium text-sm transition-colors text-white"
            style={{ backgroundColor: EFFORT_COLOR }}
          >
            {remaining > 0 ? `Next (${remaining} more)` : 'Continue'}
          </button>
          {shareCardsEnabled && current.success && (
            <button
              onClick={() => setShareMoment(momentFromGoal(current.goal, shareTemplates))}
              className="inline-flex items-center gap-1.5 py-1 text-sm font-medium text-growth/60 hover:text-growth transition-colors"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M8 1.5V10M8 1.5L5 4.5M8 1.5L11 4.5M2.5 9V12.5C2.5 13.3 3.2 14 4 14H12C12.8 14 13.5 13.3 13.5 12.5V9" />
              </svg>
              <span>share this win</span>
            </button>
          )}
        </div>
      </div>

      {shareMoment && (
        <ShareComposerSheetLazy moment={shareMoment} elevated onClose={() => setShareMoment(null)} />
      )}

      <style>{`
        @keyframes goalSeedFloat {
          0% {
            opacity: 0.9;
            transform: translate(0, 0) rotate(0deg) scale(1);
          }
          30% {
            opacity: 0.8;
            transform: translate(var(--seed-drift), calc(var(--seed-end-y) * 0.3)) rotate(calc(var(--seed-rotation) * 0.3)) scale(0.9);
          }
          70% {
            opacity: 0.5;
          }
          100% {
            opacity: 0;
            transform: translate(var(--seed-end-x), var(--seed-end-y)) rotate(var(--seed-rotation)) scale(0.3);
          }
        }
        @keyframes goalFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-goalFadeIn {
          animation: goalFadeIn 0.5s ease-out both;
        }
      `}</style>
    </div>
  );
};
